import React from 'react';
import { cn, CHECKLIST_STATUS } from '@/lib/utils';
import {
  Circle, Loader, Clock, CheckCircle, XCircle, SkipForward,
  Globe, Mail, Terminal, Timer, Wrench
} from 'lucide-react';
import { Progress } from '@/components/ui/progress';

const statusIcons = {
  Circle, Loader, Clock, CheckCircle, XCircle, SkipForward
};

const methodIcons = {
  web_form: Globe,
  email: Mail,
  api: Terminal,
  auto_expire: Timer,
  manual: Wrench,
};

export function ChecklistRail({ items = [] }) {
  const total = items.length;
  const completed = items.filter(i => ['success', 'failed', 'skipped'].includes(i.status)).length;
  const progress = total > 0 ? (completed / total) * 100 : 0;

  return (
    <div data-testid="checklist-rail">
      {/* Progress bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">
            Methods Progress
          </span>
          <span className="text-xs text-muted-foreground font-mono">
            {completed}/{total}
          </span>
        </div>
        <Progress value={progress} className="h-1.5" />
      </div>

      {/* Items */}
      <div className="space-y-2">
        {items.map((item, idx) => {
          const statusCfg = CHECKLIST_STATUS[item.status] || CHECKLIST_STATUS.pending;
          const StatusIcon = statusIcons[statusCfg.icon] || Circle;
          const MethodIcon = methodIcons[item.method] || Wrench;

          return (
            <div
              key={item.id || idx}
              className={cn(
                'flex items-start gap-3 p-3 rounded-lg border transition-colors',
                item.status === 'trying' && 'border-blue-400/30 bg-blue-400/5',
                item.status === 'success' && 'border-emerald-400/30 bg-emerald-400/5',
                item.status === 'failed' && 'border-red-400/20 bg-red-400/5',
                item.status === 'waiting' && 'border-amber-400/20 bg-amber-400/5',
                !['trying', 'success', 'failed', 'waiting'].includes(item.status) && 'border-border'
              )}
              data-testid={`checklist-item-${idx}`}
            >
              <div className={cn('mt-0.5 flex-shrink-0', statusCfg.color)}>
                <StatusIcon className={cn(
                  'w-4 h-4',
                  item.status === 'trying' && 'animate-spin'
                )} />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <MethodIcon className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="text-sm font-medium capitalize">
                    {item.method?.replace('_', ' ')}
                  </span>
                  <span className={cn('text-xs', statusCfg.color)}>
                    {statusCfg.label}
                  </span>
                </div>

                {item.target && (
                  <p className="text-xs font-mono text-muted-foreground truncate">
                    {item.target}
                  </p>
                )}

                {item.result && (
                  <p className="text-xs text-muted-foreground mt-1 break-words">
                    {item.result}
                  </p>
                )}

                <div className="flex items-center gap-3 mt-1.5">
                  <span className="text-xs text-muted-foreground">
                    Confidence: {item.confidence}%
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
